import React, {
  forwardRef,
  Ref,
  useContext,
  useRef,
  useImperativeHandle,
  useEffect,
  useState,
  useMemo
} from 'react';
import PopoverOld from '../Popover';
import { Popover } from './atoms/Popover';
import { InputDatePicker } from './index';
import Calendar from './atoms/Calendar';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import {
  CommonDateProps,
  DateModifier,
  DayData,
  EffectSingleDatePicker,
  EventSingleDatePicker,
  StateSingleDatePicker
} from './types';
import { useButton } from '@react-aria/button';
import { datepickerReducer } from './datepickerReducer';
import CalendarHeader from './atoms/CalendarHeader';
import CalendarContent from './atoms/CalendarContent';
import WeekDayNames from './atoms/WeekDayNames';
import CalendarFooter, { defaultFlexibleDates } from './atoms/CalendarFooter';
import { EffectsMap, useEffectReducer } from 'use-effect-reducer';
import dayjs from 'dayjs';
import { isDayBefore, isFutureOrPresentDay } from '../../../helpers/date';
import { CalendarProvider, useEffectOnlyOnce, useGetHandleClickOutPopup } from './atoms/hooks';
import {
  generateMonthList,
  getAddDays,
  isDayBetween,
  isDayInRange,
  isSameDay,
  transformBlackoutDaysToDate,
  formatDate,
  isValidDate,
  isDateOutOfView,
  subMonthsNumber
} from './atoms/helper';
import { getRelativeDateLocalization } from '../../../helpers/localization/localizationUtils';

import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import { ThemeProvider } from '@front10/base-elements';
import Box from '../../elements/Box';
import { useIsMobileView } from '@front10/utils';
import { getUuid } from '@front10/helpers/dist/uuid';

const NOT_AVALIABLES_DAYS = -1;
const DATE_OUT_OF_RANGE = 0;
const INCORRECT_FORMAT = 1;
const VALID_DATE = 2;

export interface OnChangeDataEvent {
  value?: Date;
  flexibleDateValue?: number | string;
}
interface ImperativeActions {
  clear: () => void;
}

export type SingleDatePickerProps = CommonDateProps & {
  /**
   * Calendar settings
   */
  calendarProps: any;
  /**
   * Default settings for both inputs
   */
  inputItem: any;
  /**
   *  Input placeholder key for input
   */
  inputPlaceholderLabelKey: string;

  /**
   * default value of date
   */
  defaultValue: Date;
  value: any;

  /**
   * onClear ranges
   */
  onClear: Function;
  /**
   * onChange function
   */
  onChange: Function;
  /*
   * onInputChange function
   */
  onInputChange: Function;

  /*
   * setDirtyDateSelector function
   */
  setDirtyDateSelector: (value: boolean) => void;

  /**
   * Patter to format date
   */
  focusReferences: Function;
  /**
   * function fired when focus the component
   */
  onFocus: Function;

  /**
   * Placeholder of the departure date
   */
  departInputPlaceholderLabelKey: string;
  /**
   * PLaceholder of the return date
   */
  returnInputPlaceholderLabelKey: string;
  /**
   * Close icon
   */
  iconClose: string;
  /**
   * Label key of input.
   */
  labelKey: string;
  /**
   * Show label of input or not
   */
  showLabel: boolean;
  /**
   * Label Settings
   */
  textLabel: any;
  /**
   * clear button settings
   */
  clearButton: any;
  /*
   * navigationItem settings
   */
  navigationItem: any;
  isMobile: boolean;
  style: any;
  availableAtoms: Array<string>;
  footer: {
    elementsToShow: Array<string>;
    labelKeyInformation?: string;
  };
  toggleElement: any;
  weekDaysContainer?: any;
  calendarContainer?: any;
  calendarMonthContainer?: any;
  setTopAtoms?: Array<string>;
  showYearCaption?: boolean;
  departure: {
    start: number;
    end: number;
  };
  openPopupOnFocus?: boolean;
  isRequired?: boolean;
  isClearEmptyInput?: boolean;
  openPopupOnKeyDown?: boolean;
  openPopupOnClick?: boolean;
  showWeekDayNames?: boolean;
  callToAction?: any;
  topAtomsContainer?: any;
  showClearInputButton?: boolean;
  changePopoverState?: (popoverState: boolean) => void;
  flexibleDates?: number;
  enabledExperimentalPopover?: boolean;
  momentParseStrictMode?: boolean;
  dayWidthSetting?: {
    enableDynamicWidthCell?: boolean;
    width?: number;
  };
  children?: any;
  minDate?: Date;
  maxDate?: Date;
};

function SingleDatePicker(
  {
    setDirtyDateSelector,
    allowedCharacters = '[0-9A-Za-z ]',
    availableDays = 45,
    captionItem = {},
    calendarProps = {
      position: 'relative'
    },
    checkedFlexibleDates = false,
    clearButton = {},
    enableFlexibleDates = false,
    flexibleDatesRangeValues = { ...defaultFlexibleDates },
    focusClass = '',
    footerCheckboxComponent = {},
    footerContainerComponent = {
      style: {
        display: 'flex'
      }
    },
    callToAction = {},
    footerSelectComponent = {},
    footerDoneButtonComponent = {},
    footerClearButtonComponent = {},
    format = 'MM/DD/YYYY',
    focusReferences = () => {},
    iconClose = 'close',
    inputItem = {
      closePopoverAfterAction: false
    },
    invalidClass = 'fr-input-invalid',
    inputDateScreenReader = 'MMMM D, YYYY',
    inputPlaceholderLabelKey = 'cmp-label--datepicker-depart',
    labelKey = 'cmp-date-range-selector-label',
    modifiersColors = {
      disabled: {
        textColor: null,
        'cmp-styles': {
          color: '#cacaca'
        }
      },
      preSelected: {
        bgColor: 'white',
        borderColor: 'colorPrimary',
        textColor: 'colorText'
      },
      selected: {
        borderColor: 'colorPrimary',
        bgColor: 'colorPrimary',
        textColor: 'white'
      },
      beginRange: {
        bgColor: 'primary',
        textColor: 'white',
        padding: '2',
        'cmp-styles': {
          'border-top-left-radius': '5px',
          'border-bottom-left-radius': '5px'
        }
      },
      endRange: {
        bgColor: 'primary',
        textColor: 'white',
        padding: '2',
        'cmp-styles': {
          'border-top-right-radius': '5px',
          'border-bottom-right-radius': '5px'
        }
      },
      today: {
        fontWeight: 'bold'
      },
      past: {
        textColor: null,
        'cmp-styles': {
          color: '#cacaca'
        }
      },
      blackout: {}
    },
    navigationItem = { marginWidth: '1' },
    navColor = 'link',
    numberOfMonths: configuredNumberOfMonths = 2,
    onClear = () => {},
    onChange = () => {},
    onFocus = () => {},
    onInputChange = () => {},
    onValueClass = 'fr-value-selected',
    popover = {},
    renderDay = null,
    renderWeekdayElement = null,
    renderNavbarElement = null,
    showInOneColumn = false,
    showLabel = false,
    showRemainingDays = false,
    textLabel = {},
    rightIcon = {},
    rightIconOpen = {},
    defaultValue = undefined,
    dayItems = {
      padding: '2'
    },
    weekdayItem = {
      weekDayFormat: 'oneLetter',
      fontWeight: 'bold',
      border: false
    },
    blackoutDays = [],
    blackoutDaysFormat = format,
    availableAtoms = enableFlexibleDates
      ? ['CalendarHeader', 'Calendar', 'Footer']
      : ['CalendarHeader', 'Calendar'],
    footer = {
      elementsToShow: enableFlexibleDates ? ['FlexibleDates', 'DoneButton', 'CancelButton'] : [],
      labelKeyInformation: 'cmp-datePicker-text-information'
    },
    setTopAtoms = ['InputDate', 'WeekDayNames'],
    toggleElement,
    footerInformationTextComponent,
    showYearCaption = true,
    departure = {
      start: 0,
      end: 180
    },
    openPopupOnFocus = false,
    openPopupOnClick = true,
    openPopupOnKeyDown = true,
    errorLabelComponent,
    showWeekDayNames = true,
    weekDaysContainer,
    calendarContainer,
    calendarMonthContainer,
    topAtomsContainer,
    changePopoverState,
    showClearInputButton = true,
    isRequired = false,
    isClearEmptyInput = false,
    flexibleDates = 0,
    enabledExperimentalPopover = false,
    momentParseStrictMode = true,
    dayWidthSetting = {
      enableDynamicWidthCell: false,
      width: 39
    },
    children,
    minDate,
    maxDate,
    ...props
  }: Partial<SingleDatePickerProps>,
  ref: Ref<ImperativeActions>
) {
  var isSameOrBefore = require('dayjs/plugin/isSameOrBefore');
  dayjs.extend(isSameOrBefore);
  const context = useContext(Context);
  const { translate, localizationData, moment: ingloriousMomentNeverUseMe } = context;
  // IMPORTANT:
  // When working with dates, the locale that must be imported
  // into dayjs or moment is the one returned by the localizationUtils,
  // as is the case for the fareItemLastSeen (FareItemLastSeen.jsx),
  // since it may have some modifications from the translations API.
  const relativeTime = getRelativeDateLocalization(translate, localizationData);
  dayjs.locale(relativeTime);
  if (
    relativeTime.name !== 'en' &&
    ingloriousMomentNeverUseMe &&
    typeof ingloriousMomentNeverUseMe.locale === 'function'
  ) {
    ingloriousMomentNeverUseMe.locale(relativeTime.name, relativeTime);
  }

  let isMobile = useIsMobileView();
  if (props.isMobile) {
    isMobile = true;
  }

  useEffect(() => {
    if (!isMobile && state.months[0] !== undefined) {
      const newDate = state.currentDate?.start
        ? state.currentDate.start
        : state.preSelected ?? state.months[0].firstAvailableDay() ?? dayjs().toDate();
      dispatch({
        type: 'SET_DISPLAY_DATE_ACTION',
        payload: newDate
      });
      dispatch({
        type: 'SET_PRESELECTED_DATE',
        payload: newDate
      });
      if (
        state.inputValue.start !== null &&
        state.inputValue.start !== undefined &&
        state.inputValue.start !== ''
      ) {
        dispatch({
          type: 'SET_INPUT_VALUE',
          payload: dayjs(newDate).format(format),
          component: 'start'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const numberOfMonths = useMemo(() => {
    if (departure?.start != null && isMobile) {
      const startDate = minDate ? dayjs(minDate) : dayjs().add(departure.start, 'day');
      const endDate = maxDate
        ? dayjs(maxDate)
        : dayjs().add(departure.start + departure.end - 1, 'day');
      let monthDiff = endDate.diff(startDate, 'month');
      if (monthDiff === 0) {
        return configuredNumberOfMonths;
      }
      monthDiff = 0;
      const initialDate = dayjs(new Date(startDate.get('year'), startDate.get('month'), 1));
      let tmpEndDate = initialDate;
      while (tmpEndDate.isSameOrBefore(endDate, 'day')) {
        monthDiff++;
        tmpEndDate = initialDate.add(monthDiff, 'month');
      }
      return monthDiff;
    } else {
      return configuredNumberOfMonths;
    }
  }, [departure.start, departure.end, isMobile, minDate, maxDate, configuredNumberOfMonths]);

  const initialState: StateSingleDatePicker = {
    displayDate: dayjs().toDate(),
    previousDate: null,
    selectedComponent: 'start',
    availableDays: departure ? departure.start + departure.end : 0,
    months: [],
    today: dayjs().toDate(),
    currentDate: defaultValue ? { start: defaultValue ?? undefined } : null,
    preSelected: defaultValue ? defaultValue : undefined,
    selectedRange: null,
    popoverOpen: false,
    isCheckedFlexibleDates: checkedFlexibleDates,
    flexibleValue: flexibleDatesRangeValues?.defaultValue,
    inputValue: {
      start: defaultValue ? formatDate(dayjs(defaultValue).toDate(), format, context) : undefined,
      end: undefined
    },
    valid: true,
    validEnd: true,
    typeError: VALID_DATE,
    focusSelectedDay: false,
    enableFlexibleDates: enableFlexibleDates,
    blackoutDays: transformBlackoutDaysToDate(blackoutDays, blackoutDaysFormat, context),
    currentMonth: 0,
    validateDone: false,
    userInteraction: false,
    tabActionOnInput: false,
    currentMonthDateA11y: defaultValue ? dayjs(defaultValue).toDate() : undefined
  };
  const reduceEffectMap: EffectsMap<
    StateSingleDatePicker,
    EventSingleDatePicker,
    EffectSingleDatePicker
  > = {
    CLEAR_ACTION_DEPARTURE: (state) => {},
    CLEAR_ACTION_RETURN: (state) => {},
    RESET_ACTION_AND_CLEAR_STATE: (state) => {
      const { flexibleValue: stateFlexibleDateValue, isCheckedFlexibleDates } = state;
      const mergedFlexibleValue = isCheckedFlexibleDates ? stateFlexibleDateValue : 0;
      if (props?.value?.start) {
        onChange({
          start: undefined,
          end: undefined,
          flexibleDates: mergedFlexibleValue,
          availableDays: state.availableDays || NOT_AVALIABLES_DAYS,
          enabledFlexibleDates: enableFlexibleDates,
          checkedFlexibleDates: isCheckedFlexibleDates
        });
      }
    },
    SET_CURRENT_DATE: (state) => {},
    RESET_DATA_ACTION: (state) => {},
    CHANGE_ACTION: (state) => {},
    SET_CURRENT_MONTH_DATE_A11Y: (state) => {},
    SET_DISPLAY_DATE_ACTION: (state) => {
      let lastDate = getAddDays(
        state.today,
        departure?.end ? departure?.start + departure?.end - 1 : 0
      );
      if (maxDate) {
        lastDate = dayjs(maxDate).toDate();
      }

      const isEnabledNextMonth = isDateOutOfView(state.displayDate, numberOfMonths, lastDate);
      const months = generateMonthList({
        displayDate: isEnabledNextMonth
          ? state.displayDate
          : !isMobile
          ? dayjs(state.displayDate).get('month') === dayjs(lastDate).get('month') &&
            numberOfMonths === 1
            ? state.displayDate
            : subMonthsNumber(state.displayDate, 1)
          : state.displayDate,
        modifiers,
        modifiersColors,
        numberOfMonths: numberOfMonths,
        blackOutDays: state.blackoutDays,
        showRemainingDays,
        locale: localizationData
      });
      dispatch({ type: 'SET_MONTHS', payload: months });
      if (state.currentMonth) {
        let newCurrentMonth = state.currentMonth + 1;
        if (state.currentMonth < 0 && newCurrentMonth > 0) {
          newCurrentMonth = -newCurrentMonth;
        } else if (state.currentMonth < 0 && newCurrentMonth === 0) {
          newCurrentMonth = state.currentMonth - 1;
        }
        dispatch({
          type: 'CHANGE_CURRENT_MONTH',
          payload: newCurrentMonth
        });
      }
      if (!isMobile && !isEnabledNextMonth && numberOfMonths > 1) {
        dispatch({
          type: 'SET_DISPLAY_DATE_ACTION',
          payload: subMonthsNumber(state.displayDate, 1)
        });
      }
    },
    CHANGE_CURRENT_MONTH: (state) => {},
    SET_DATE_ACTION: (state, action) => {
      throwOnChangeEvent();
      if (action.close) {
        if (enabledExperimentalPopover) {
          overlayState.close();
        } else {
          popoverRef?.current?.closePopover();
        }
      }
    },
    CLEAR_ACTION: (state) => {
      throwOnChangeEvent();

      if (state.enableFlexibleDates && checkedFlexibleDates) {
        dispatch({ type: 'SET_FLEXIBLE_DATES_CHECKED', payload: false });
      }
      inputRef?.current?.focus();
      onClear();
    },
    RESET_ACTION: (state) => {},
    CANCEL_ACTION: (state) => {
      if (state.currentDate?.start) {
        dispatch({ type: 'SET_PRESELECTED_DATE', payload: state.currentDate?.start });
        dispatch({
          type: 'SET_INPUT_VALUE',
          payload: state.currentDate?.start ? dayjs(state.currentDate.start).format(format) : '',
          component: 'start'
        });
      }
      if (enabledExperimentalPopover) {
        overlayState.close();
      } else {
        popoverRef?.current?.closePopover();
      }
    },
    UNDO_ACTION: (state) => {
      if (state.currentDate?.start) {
        dispatch({ type: 'SET_PRESELECTED_DATE', payload: state.currentDate?.start });
        dispatch({
          type: 'SET_INPUT_VALUE',
          payload: state.currentDate?.start ? dayjs(state.currentDate.start).format(format) : '',
          component: 'start'
        });
      }
      if (enabledExperimentalPopover) {
        overlayState.close();
      } else {
        popoverRef?.current?.closePopover();
      }
    },
    SET_PRESELECTED_DATE: (state, action) => {
      dispatch({
        type: 'SET_INPUT_VALUE',
        payload: dayjs(action.payload).format(format),
        component: 'start'
      });
    },
    SELECT_DAY_ACTION: (state, action) => {
      dispatch({
        type: 'SET_INPUT_VALUE',
        payload: dayjs(action.payload.start).format(format),
        component: 'start'
      });
      if (!enableFlexibleDates && !isMobile) {
        throwOnChangeEvent();
        if (enabledExperimentalPopover) {
          overlayState.close();
        } else {
          popoverRef?.current?.closePopover();
        }
      }
    },
    PARSE_INPUT_ACTION: (state) => {
      dayjs.extend(customParseFormat);
      let value = state.inputValue.start;
      value = value?.toLowerCase();
      if (value && value?.charAt(0) >= 'a' && value?.charAt(0) <= 'z') {
        value = value?.charAt(0).toUpperCase() + value.substring(1, value.length);
      }

      const isCorrectFormat = ingloriousMomentNeverUseMe(
        value,
        format,
        momentParseStrictMode
      ).isValid();
      const date = ingloriousMomentNeverUseMe(value, format).toDate();
      if (isCorrectFormat && !!date && isFutureOrPresentDay(date) && isValidDate(date, modifiers)) {
        if (state.validateDone) {
          dispatch({
            type: 'SET_PRESELECTED_DATE',
            payload: date
          });
          dispatch({ type: 'VALIDATE_DONE' });
        } else {
          dispatch({ type: 'SET_DATE_ACTION', payload: date, component: 'start', close: true });
        }
        dispatch({ type: 'SET_TYPE_ERROR', payload: VALID_DATE });
        return;
      } else {
        if (!isCorrectFormat) {
          dispatch({ type: 'SET_TYPE_ERROR', payload: INCORRECT_FORMAT });
        } else if (!isFutureOrPresentDay(date) || !isValidDate(date, modifiers)) {
          dispatch({ type: 'SET_TYPE_ERROR', payload: DATE_OUT_OF_RANGE });
        }
      }
      dispatch({ type: 'SET_VALID', payload: false });

      if (state.validateDone) {
        dispatch({ type: 'VALIDATE_DONE' });
      }
    },
    CLOSE_POPOVER_ACTION: () => {
      if (enabledExperimentalPopover) {
        overlayState.close();
      } else {
        popoverRef?.current?.closePopover();
      }
    },
    OPEN_POPOVER_ACTION: () => {
      if (enabledExperimentalPopover) {
        if (!isCloseRef.current) {
          overlayState.open();
        }
        isCloseRef.current = false;
      } else {
        popoverRef?.current?.openPopover();
      }
    }
  };

  const [state, dispatch] = useEffectReducer<
    StateSingleDatePicker,
    EventSingleDatePicker,
    EffectSingleDatePicker
  >(datepickerReducer, initialState, reduceEffectMap);
  const [accessibilityState, setAccessibilityState] = useState(false);
  //refs
  const popoverRef = useRef<any>(null);
  const wasOpenPopupRef = useRef<any>(false);
  const flexibleFilterContainerRef = useRef<any>(null);
  const inputRef = useRef<HTMLElement>(null);
  const containerRef = useRef<any>(null);
  const componentReference = useRef<HTMLDivElement>(null);
  const isCloseRef = useRef<any>(false);

  const setWasOpenPopupRef = (value: boolean) => {
    wasOpenPopupRef.current = value;
  };

  const { theme, direction } = context;
  const [className, style] = useStyles(props, { theme, direction });

  //set modifiers to be used by datePicker
  const modifiers: Partial<DateModifier> = {
    today: (day: Date) => isSameDay(day, state.today),
    beginRange: (day: Date) => {
      return isSameDay(day, state.preSelected);
    },
    endRange: (day) => isSameDay(day, state.preSelected),
    selected: (day) =>
      isDayInRange(day, { start: state.currentDate?.start, end: state.currentDate?.start }) &&
      (state.preSelected === undefined ||
        (state.preSelected && dayjs(day).isSame(state.preSelected, 'day'))),
    past: (day) => isDayBefore(day, state.today),
    disabled: {
      before: minDate
        ? isDayBefore(dayjs(minDate).toDate(), state.today)
          ? state.today
          : dayjs(minDate).toDate()
        : departure && departure.start
        ? dayjs(state.today).add(departure.start, 'day').toDate()
        : state.today,
      after: maxDate
        ? dayjs(maxDate).toDate()
        : departure && departure.end && departure.start
        ? dayjs(state.today)
            .add(departure.end + departure.start - 1, 'day')
            .toDate()
        : getAddDays(state.today, state.availableDays - 1)
    },
    blackout: (date) => {
      return state.blackoutDays.some((current) => {
        return isDayBetween(date, current.start, current.end, true);
      });
    },
    preSelected: (day) => isSameDay(day, state.preSelected)
  };

  useEffectOnlyOnce(() => {
    dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: new Date() });
  }, [dispatch]);

  useEffect(() => {
    if (
      state.inputValue.start === '' ||
      state.inputValue.start === undefined ||
      state.inputValue.start === null
    ) {
      if (setDirtyDateSelector) {
        setDirtyDateSelector(false);
      }
    } else {
      if (setDirtyDateSelector) {
        setDirtyDateSelector(true);
      }
    }
  }, [setDirtyDateSelector, state.inputValue.start]);

  //input events
  const handleDayClick = (dayData: DayData) => {
    dispatch({
      type: 'SET_USER_INTERACTION_ACTION',
      payload: true
    });
    dispatch({
      type: 'SET_INPUT_VALUE',
      payload: dayjs(dayData.date).format(format),
      component: 'start'
    });
    const enabledFooter = availableAtoms.find((e) => e === 'Footer');
    if (isMobile || enabledFooter !== undefined) {
      dispatch({ type: 'SET_PRESELECTED_DATE', payload: dayData.date });
      return;
    }
    dispatch({
      type: 'SELECT_DAY_ACTION',
      payload: { start: dayData.date }
    });
    if (enabledFooter === undefined) {
      handleClickOutPopup(
        {
          ...state,
          preSelected: dayData.date,
          currentDate: { start: dayData.date, end: undefined },
          userInteraction: true
        },
        dispatch
      );
      if (enabledExperimentalPopover) {
        overlayState.close();
      } else {
        popoverRef?.current?.closePopover();
      }
    }
  };

  const handleInputChange = (event: any) => {
    dispatch({ type: 'SET_INPUT_VALUE', payload: event.target.value, component: 'start' });
    onInputChange(event);
  };

  //popover events
  const handleClosePopover = () => {
    changePopoverState?.(false);
    dispatch({
      type: 'SET_POPOVER',
      payload: { popoverOpen: false, focusSelectedDay: false }
    });
    if (enableFlexibleDates || footer.elementsToShow.length > 0) {
      dispatch({ type: 'SET_PRESELECTED_DATE', payload: state.currentDate?.start });
      dispatch({
        type: 'SET_INPUT_VALUE',
        payload: state.currentDate ? dayjs(state.currentDate.start).format(format) : '',
        component: 'start'
      });
    }
    dispatch({
      type: 'SET_USER_INTERACTION_ACTION',
      payload: false
    });
    wasOpenPopupRef.current = true;
    inputRef?.current?.focus();
  };

  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const handleOpenPopover = () => {
    changePopoverState?.(true);
    dispatch({
      type: 'SET_POPOVER',
      payload: { popoverOpen: true, focusSelectedDay: false }
    });
    dispatch({
      type: 'SET_USER_INTERACTION_ACTION',
      payload: false
    });
    if (toggleElement !== undefined && toggleElement !== null) {
      inputRef?.current?.focus();
      if (state.months.length > 0) {
        if (!state.preSelected) {
          if (defaultValue) {
            dispatch({
              type: 'SET_INPUT_VALUE',
              payload: dayjs(defaultValue).format(format),
              component: 'start'
            });
            dispatch({
              type: 'SET_PRESELECTED_DATE',
              payload: defaultValue
            });
            dispatch({
              type: 'SET_CURRENT_DATE',
              payload: {
                ...state.currentDate,
                start: defaultValue
              }
            });
          }
        } else {
          if (defaultValue !== undefined) {
            if (!dayjs(state.preSelected).isSame(defaultValue, 'day')) {
              dispatch({
                type: 'SET_INPUT_VALUE',
                payload: dayjs(defaultValue).format(format),
                component: 'start'
              });
              dispatch({
                type: 'SET_PRESELECTED_DATE',
                payload: defaultValue
              });
              dispatch({
                type: 'SET_CURRENT_DATE',
                payload: {
                  ...state.currentDate,
                  start: defaultValue
                }
              });
            }
          } else {
            dispatch({ type: 'CLEAR_ACTION' });
          }
        }
      }
    }
    if (enableFlexibleDates && toggleElement !== undefined) {
      dispatch({ type: 'SET_FLEXIBLE_DATES_CHECKED', payload: checkedFlexibleDates });
      if (flexibleDates !== 0) {
        dispatch({ type: 'SET_FLEXIBLE_DATES_VALUE', payload: flexibleDates });
      }
    }
  };
  //component events
  const throwOnChangeEvent = ({ value, flexibleDateValue = 0 }: OnChangeDataEvent = {}) => {
    const {
      currentDate: stateValue,
      flexibleValue: stateFlexibleDateValue,
      isCheckedFlexibleDates
    } = state;

    const mergedValue = value ? value : stateValue?.start;
    const mergedFlexibleValue = isCheckedFlexibleDates
      ? flexibleDateValue || stateFlexibleDateValue
      : 0;
    onChange({
      start: mergedValue,
      end: undefined,
      flexibleDates: mergedFlexibleValue,
      availableDays: state.availableDays || NOT_AVALIABLES_DAYS,
      enabledFlexibleDates: enableFlexibleDates,
      checkedFlexibleDates: isCheckedFlexibleDates
    });
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      dispatch({ type: 'CLEAR_ACTION' });
    },
    focusInput: () => {
      inputRef.current?.focus();
    },
    reset: () => {
      dispatch({ type: 'RESET_ACTION' });
    },
    validate: () => {
      dispatch({ type: 'PARSE_INPUT_ACTION', payload: '', component: 'start' });
    },
    valid: () => {
      return state.valid;
    },
    focusToggleElement: () => {
      if (enabledExperimentalPopover) {
        triggerRef.current?.focus();
      }
    }
  }));

  const renderInput = () => (
    <InputDatePicker
      errorLabelComponent={errorLabelComponent}
      allowedCharacters={allowedCharacters}
      className={className}
      style={style}
      inputItem={inputItem}
      inputRef={inputRef}
      componentReference={componentReference}
      clearButton={clearButton}
      focusClass={focusClass}
      invalidClass={invalidClass}
      labelKey={labelKey}
      showLabel={showLabel}
      clearAction={() => {
        dispatch({ type: 'CLEAR_ACTION' });
      }}
      format={format}
      onChange={handleInputChange}
      onDayClick={handleDayClick}
      iconClose={iconClose}
      rightIcon={rightIcon}
      rightIconOpen={rightIconOpen}
      showCloseIcon={
        showClearInputButton &&
        state.inputValue?.start !== '' &&
        state.inputValue?.start !== undefined &&
        state.inputValue?.start !== null
      }
      textLabel={textLabel}
      valid={state.valid}
      onValueClass={onValueClass}
      inputPlaceholderLabelKey={inputPlaceholderLabelKey}
      openPopupOnKeyDown={openPopupOnKeyDown}
      openPopupOnClick={openPopupOnClick}
      firstAvailableDay={departure.start ? departure.start : 0}
      onBlur={() => {
        if (state.inputValue.start === '' && !state.popoverOpen) {
          dispatch({ type: 'RESET_ACTION' });
        }
        if (!state.popoverOpen) {
          wasOpenPopupRef.current = false;
        }

        if (toggleElement === null || toggleElement === undefined) {
          if (inputRef?.current?.getAttribute('value')?.length === 0) {
            setAccessibilityState(true);
          }
        }
      }}
      onFocus={() => {
        if (!wasOpenPopupRef.current && openPopupOnFocus) {
          dispatch({ type: 'OPEN_POPOVER_ACTION' });
          wasOpenPopupRef.current = false;
        }
        if (wasOpenPopupRef) {
          dispatch({
            type: 'SET_INPUT_VALUE',
            payload:
              state.currentDate?.start && state.typeError === VALID_DATE
                ? dayjs(state.currentDate.start).format(format)
                : '',
            component: 'start'
          });
          dispatch({
            type: 'SET_PRESELECTED_DATE',
            payload: state.currentDate?.start ? state.currentDate.start : undefined
          });
          wasOpenPopupRef.current = true;
        }
        setTimeout(() => {
          isCloseRef.current = false;
        }, 100);
        onFocus();
        if (toggleElement === null || toggleElement === undefined) {
          setAccessibilityState(false);
        }
      }}
      onClick={(e: any) => {
        if (enabledExperimentalPopover) {
          if (isOpenPopover) {
            overlayState.close();
          } else {
            overlayState.open();
          }
          setIsOpenPopover(!isOpenPopover);
        }
      }}
      onKeyDown={(e: any) => {
        if (enabledExperimentalPopover) {
          if (e.keyCode === 40) {
            if (!isOpenPopover) {
              overlayState.open();
              setIsOpenPopover(!isOpenPopover);
            }
          }
        }
      }}
      isRequired={isRequired}
      isClearEmptyInput={isClearEmptyInput}
      buttonClearId={buttonClearId}
      buttonRightIconId={buttonRightIconId}
      children={children}
    />
  );

  const setUserInteraction = (value: boolean) => {
    dispatch({
      type: 'SET_USER_INTERACTION_ACTION',
      payload: value
    });
  };
  const buttonRightIconId = `date-picker-button-right-${getUuid()}`;
  const buttonClearId = `date-picker-clear-button-${getUuid()}`;

  const elements: Record<string, any> = {
    InputDate: (
      <InputDatePicker
        firstAvailableDay={departure.start ? departure.start : 0}
        errorLabelComponent={errorLabelComponent}
        allowedCharacters={allowedCharacters}
        className={className}
        style={style}
        inputItem={inputItem}
        inputRef={inputRef}
        componentReference={componentReference}
        clearButton={clearButton}
        onBlurRightnButton={() => {
          if (inputRef?.current?.getAttribute('value')?.length !== 0) {
            setAccessibilityState(true);
          }
        }}
        focusClass={focusClass}
        invalidClass={invalidClass}
        labelKey={labelKey}
        showLabel={showLabel}
        clearAction={() => {
          dispatch({ type: 'CLEAR_ACTION' });
          inputRef?.current?.focus();
        }}
        format={format}
        onChange={handleInputChange}
        onFocus={() => {
          onFocus();
          setAccessibilityState(false);
        }}
        handleKeyDown={(event: any) => {
          if (event.keyCode === 9) {
            dispatch({ type: 'SET_TAB_ACTION_INPUT', payload: true });
          }
        }}
        onBlur={() => {
          if (inputRef?.current?.getAttribute('value')?.length === 0) {
            setAccessibilityState(true);
          }
          if (Boolean(state.inputValue?.start) && state.valid) {
            dispatch({ type: 'VALIDATE_DONE' });
            let event = new KeyboardEvent('onKeyPress', { key: 'Enter', code: 'Enter' });
            inputRef.current?.dispatchEvent(event);
          }
        }}
        onDayClick={handleDayClick}
        iconClose={iconClose}
        closePopoverAfterAction={inputItem.closePopoverAfterAction}
        rightIcon={rightIcon}
        rightIconOpen={rightIconOpen}
        showCloseIcon={showClearInputButton && !!state.inputValue?.start?.length}
        textLabel={textLabel}
        valid={state.valid}
        onValueClass={onValueClass}
        inputPlaceholderLabelKey={inputPlaceholderLabelKey}
        buttonRightIconId={buttonRightIconId}
        buttonClearId={buttonClearId}
      />
    ),
    CalendarHeader: (
      <CalendarHeader
        {...navigationItem}
        dispatch={dispatch}
        state={state}
        format={format}
        currentMonth={state.currentMonth}
        accessibilityState={accessibilityState}
        numberOfMonths={numberOfMonths}
        currentMonthDateA11y={state.currentMonthDateA11y}
        isPrimaryFilter={toggleElement === null || toggleElement === undefined}
        setPreselectedDate={() => {
          if (state.months.length > 0) {
            if (!state.preSelected) {
              const newDate = departure.start
                ? getAddDays(state.today, departure.start)
                : state.today;
              dispatch({
                type: 'SET_PRESELECTED_DATE',
                payload: state.months[0].firstAvailableDay() ?? newDate
              });
              setTimeout(() => {
                dispatch({
                  type: 'SET_USER_INTERACTION_ACTION',
                  payload: true
                });
              }, 100);
            } else {
              dispatch({
                type: 'SET_PRESELECTED_DATE',
                payload: state.preSelected
              });
            }
            dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
          }
        }}
        minDate={minDate}
        maxDate={maxDate}
      />
    ),
    Calendar: (
      <CalendarContent
        showYear={showYearCaption}
        numberOfMonths={numberOfMonths}
        months={state.months}
        displayDate={state.displayDate}
        captionItem={captionItem}
        weekdayItem={weekdayItem}
        dayItems={dayItems}
        onDayClick={handleDayClick}
        showWeekDayNames={showWeekDayNames}
        weekDaysContainer={weekDaysContainer}
        calendarContainer={calendarContainer}
        calendarMonthContainer={calendarMonthContainer}
        setWasOpenPopup={setWasOpenPopupRef}
        currentDate={state.currentDate}
        dispatch={dispatch}
        preSelectedDate={state.preSelected}
        setUserInteraction={(e: boolean) => {
          setUserInteraction(e);
        }}
        componentName="SingleDatePicker"
        dayWidthSetting={dayWidthSetting}
      />
    ),
    Footer: (
      <CalendarFooter
        enableFlexibleDates={enableFlexibleDates}
        checkedFlexibleDates={state.isCheckedFlexibleDates}
        flexibleDatesRangeValues={flexibleDatesRangeValues}
        footerCheckboxComponent={footerCheckboxComponent}
        footerSelectComponent={footerSelectComponent}
        footerClearButtonComponent={footerClearButtonComponent}
        footerDoneButtonComponent={footerDoneButtonComponent}
        footerContainerComponent={footerContainerComponent}
        flexibleValue={parseInt('' + state.flexibleValue)}
        dispatch={dispatch}
        state={state}
        footerSettings={footer}
        format={format}
        footerInformationTextComponent={footerInformationTextComponent}
        firstAvailableDay={departure.start ? departure.start : 0}
      />
    )
  };

  const mobileElements: Record<string, any> = {
    InputDate: (
      <InputDatePicker
        firstAvailableDay={departure.start ? departure.start : 0}
        errorLabelComponent={errorLabelComponent}
        allowedCharacters={allowedCharacters}
        className={className}
        style={style}
        inputItem={inputItem}
        inputRef={inputRef}
        componentReference={componentReference}
        clearButton={clearButton}
        focusClass={focusClass}
        invalidClass={invalidClass}
        labelKey={labelKey}
        showLabel={showLabel}
        clearAction={() => {
          dispatch({ type: 'CLEAR_ACTION' });
        }}
        format={format}
        onChange={handleInputChange}
        onFocus={onFocus}
        onDayClick={handleDayClick}
        iconClose={iconClose}
        closePopoverAfterAction={inputItem.closePopoverAfterAction}
        rightIcon={rightIcon}
        rightIconOpen={rightIconOpen}
        showCloseIcon={showClearInputButton && !!state.inputValue?.start?.length}
        textLabel={textLabel}
        valid={state.valid}
        onValueClass={onValueClass}
        inputPlaceholderLabelKey={inputPlaceholderLabelKey}
        onBlur={() => {
          if (state.inputValue.start === '') {
            dispatch({ type: 'CLEAR_ACTION' });
          } else {
            if (state.valid) {
              let event = new KeyboardEvent('onKeyPress', { key: 'Enter', code: 'Enter' });
              inputRef.current?.dispatchEvent(event);
            }
          }
        }}
        buttonClearId={buttonClearId}
        buttonRightIconId={buttonRightIconId}
      />
    ),
    WeekDayNames: (
      <WeekDayNames weekdayItem={weekdayItem} weekDaysContainer={weekDaysContainer}></WeekDayNames>
    ),
    Calendar: (
      <CalendarContent
        isMobile={true}
        showYear={showYearCaption}
        ref={containerRef}
        numberOfMonths={numberOfMonths}
        months={state.months}
        displayDate={state.displayDate}
        captionItem={captionItem}
        weekdayItem={weekdayItem}
        dayItems={dayItems}
        onDayClick={handleDayClick}
        showWeekDayNames={showWeekDayNames}
        weekDaysContainer={weekDaysContainer}
        calendarContainer={calendarContainer}
        calendarMonthContainer={calendarMonthContainer}
        currentDate={state.currentDate}
        preSelectedDate={state.preSelected}
        setUserInteraction={(e: boolean) => {
          setUserInteraction(e);
        }}
        componentName="SingleDatePicker"
        dayWidthSetting={dayWidthSetting}
      />
    ),
    Footer: (
      <CalendarFooter
        enableFlexibleDates={enableFlexibleDates}
        checkedFlexibleDates={state.isCheckedFlexibleDates}
        flexibleDatesRangeValues={flexibleDatesRangeValues}
        footerCheckboxComponent={footerCheckboxComponent}
        footerSelectComponent={footerSelectComponent}
        footerClearButtonComponent={footerClearButtonComponent}
        footerDoneButtonComponent={footerDoneButtonComponent}
        footerContainerComponent={footerContainerComponent}
        callToAction={callToAction}
        flexibleValue={parseInt('' + state.flexibleValue)}
        dispatch={dispatch}
        state={state}
        footerSettings={footer}
        footerInformationTextComponent={footerInformationTextComponent}
        onSaveFunction={(value: boolean) => {
          return value
            ? flexibleFilterContainerRef?.current?.save()
            : flexibleFilterContainerRef?.current?.dismiss();
        }}
        isMobile={true}
        format={format}
        firstAvailableDay={departure.start ? departure.start : 0}
      />
    )
  };

  //refs
  const triggerRef = useRef<any>(null);
  let overlayState = useOverlayTriggerState({
    onOpenChange: (isOpen) => {
      if (isOpen) {
        handleOpenPopover();
      } else {
        handleClosePopover();
      }
    }
  });
  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps, updatePosition } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef: popoverRef,
    placement: 'bottom left',
    offset: 0,
    isOpen: overlayState.isOpen
  });

  let overlayTriggerState1 = { ...overlayState, close: updatePosition };

  let { triggerProps } = useOverlayTrigger({ type: 'dialog' }, overlayTriggerState1, triggerRef);

  // console.log('triggerProps', triggerProps);
  // useButton ensures that focus management is handled correctly,
  // across all browsers. Focus is restored to the button once the
  // popover closes.
  let { buttonProps } = useButton(
    {
      onPress: (e) => {
        overlayState.open();
      },
      elementType: 'div'
    },
    triggerRef
  );

  const memoState = useMemo(
    () => ({
      ...state,
      popoverOpen: overlayState.isOpen
    }),
    [state, overlayState.isOpen]
  );

  const temporalAvailableAtoms = [...availableAtoms];
  const getTopAtom = () =>
    temporalAvailableAtoms.filter((e) => setTopAtoms?.find((element) => element === e));
  const removeTopAtom = () => temporalAvailableAtoms.filter((e) => setTopAtoms?.indexOf(e) === -1);
  const footerPosition = temporalAvailableAtoms.indexOf('Footer');
  const newAvailableAtoms =
    footerPosition !== -1
      ? temporalAvailableAtoms.splice(footerPosition, 1)
      : temporalAvailableAtoms;

  const { handleClickOutPopup } = useGetHandleClickOutPopup({
    isActiveFooter: footerPosition !== -1
  });

  const newToggleElement = React.cloneElement(toggleElement ?? renderInput(), {
    ...buttonProps,
    ...triggerProps,
    ...(toggleElement ?? renderInput()).props,
    ref: triggerRef,
    componentReference: triggerRef
  });

  const [popoverStyle] = useStyles(popover, {
    direction,
    theme
  });

  useEffect(() => {
    if (enabledExperimentalPopover) {
      if (!overlayState.isOpen) {
        if (changePopoverState) {
          changePopoverState?.(overlayState.isOpen);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayState.isOpen]);

  let newPositionProps: any = { ...positionProps };
  if (newPositionProps) {
    delete newPositionProps['style']['maxHeight'];
  }
  return isMobile ? (
    <ThemeProvider theme={theme.designTokens ?? undefined}>
      <CalendarProvider
        modifiers={modifiers}
        modifiersColors={modifiersColors}
        state={state}
        dispatch={dispatch}
        format={format}
        numberOfMonths={numberOfMonths}
      >
        <FlexibleFilterContainer
          toggleElement={toggleElement ?? renderInput()}
          ref={flexibleFilterContainerRef}
          title={translate('cmp-date-picker-header-title', 'Departure date')}
          onOpen={() => {
            if (toggleElement !== undefined && toggleElement !== null) {
              if (state.months.length > 0) {
                if (!state.preSelected) {
                  if (defaultValue) {
                    dispatch({
                      type: 'SET_INPUT_VALUE',
                      payload: dayjs(defaultValue).format(format),
                      component: 'start'
                    });
                    dispatch({
                      type: 'SET_PRESELECTED_DATE',
                      payload: defaultValue
                    });
                    dispatch({
                      type: 'SET_CURRENT_DATE',
                      payload: {
                        ...state.currentDate,
                        start: defaultValue
                      }
                    });
                  }
                } else {
                  if (defaultValue !== undefined) {
                    if (!dayjs(state.preSelected).isSame(defaultValue, 'day')) {
                      dispatch({
                        type: 'SET_INPUT_VALUE',
                        payload: dayjs(defaultValue).format(format),
                        component: 'start'
                      });
                      dispatch({
                        type: 'SET_PRESELECTED_DATE',
                        payload: defaultValue
                      });
                      dispatch({
                        type: 'SET_CURRENT_DATE',
                        payload: {
                          ...state.currentDate,
                          start: defaultValue
                        }
                      });
                    }
                  } else {
                    dispatch({ type: 'CLEAR_ACTION' });
                  }
                }
              }
            }
            if (enableFlexibleDates && toggleElement !== undefined) {
              dispatch({ type: 'SET_FLEXIBLE_DATES_CHECKED', payload: checkedFlexibleDates });
              if (flexibleDates !== 0) {
                dispatch({ type: 'SET_FLEXIBLE_DATES_VALUE', payload: flexibleDates });
              }
            }
          }}
          variant="layout.popover"
          label={'label'}
          onSave={() => {
            dispatch({
              type: 'SET_DATE_ACTION',
              payload: state.preSelected ?? null,
              component: 'start',
              close: true
            });
          }}
          onDismiss={() => {
            dispatch({
              type: 'SET_DATE_ACTION',
              payload: state.preSelected ?? null,
              component: 'start',
              close: true
            });
            changePopoverState?.(false);
          }}
          onScroll={() => {}}
          footer={footer.elementsToShow ? mobileElements['Footer'] : null}
          mobileView={isMobile}
        >
          {setTopAtoms && (
            <Box
              position="absolute"
              bgColor="white"
              style={{
                left: 0,
                right: 0,
                zIndex: 100
              }}
              {...topAtomsContainer}
            >
              {getTopAtom().map((e: any) => mobileElements[e])}
            </Box>
          )}
          {setTopAtoms
            ? removeTopAtom().map((e) => mobileElements[e])
            : availableAtoms && newAvailableAtoms.map((e) => mobileElements[e])}
        </FlexibleFilterContainer>
      </CalendarProvider>
    </ThemeProvider>
  ) : (
    <>
      {enabledExperimentalPopover ? (
        <ThemeProvider theme={theme.designTokens ?? undefined}>
          <CalendarProvider
            modifiers={modifiers}
            modifiersColors={modifiersColors}
            state={memoState}
            dispatch={dispatch}
            format={format}
            numberOfMonths={numberOfMonths}
          >
            {newToggleElement}
            {overlayState.isOpen ? (
              <OverlayContainer>
                <Popover
                  {...Object.assign({ sx: { bg: 'white' } }, { ...positionProps })}
                  className={`${popoverStyle} fr-popover-direction`}
                  popoverRef={popoverRef}
                  isOpen={overlayState.isOpen}
                  onClose={() => {
                    handleClosePopover();
                    overlayState.close();
                    isCloseRef.current = true;
                  }}
                  onClickOutPopup={() => {
                    handleClickOutPopup(state, dispatch);
                  }}
                >
                  {availableAtoms && availableAtoms.map((e) => elements[e])}
                </Popover>
              </OverlayContainer>
            ) : null}
          </CalendarProvider>
        </ThemeProvider>
      ) : (
        <CalendarProvider
          modifiers={modifiers}
          modifiersColors={modifiersColors}
          state={state}
          dispatch={dispatch}
          format={format}
          numberOfMonths={numberOfMonths}
        >
          <PopoverOld
            alignLeft={true}
            element={toggleElement ?? renderInput()}
            ref={popoverRef}
            onClose={() => {
              handleClosePopover();
            }}
            onOpen={handleOpenPopover}
            minWidth="unset"
            openPressArrowDown={toggleElement === undefined}
            onClickOutPopup={() => {
              handleClickOutPopup(state, dispatch);
            }}
            hideWithOutsideClick={true}
            {...popover}
          >
            <Calendar {...calendarProps}>
              {availableAtoms && availableAtoms.map((e) => elements[e])}
            </Calendar>
          </PopoverOld>
        </CalendarProvider>
      )}
    </>
  );
}

SingleDatePicker.componentName = 'SingleDatePicker';

export default forwardRef(SingleDatePicker);
